import { fieldMeaningsBySource } from '../../generated/field-meanings';
import { propertyRecordKindLabel } from '../field-labels';
import { stableSerialize } from './facts';
import {
  fieldMeaningKey,
  type FieldMeaningDeclaration,
  type FieldMeaningKind,
  type ImmaterialReason,
  type SkillFieldMeanings,
} from './field-meanings';
import { fieldPathTail, issueFieldLabel } from './fields';
import type { IssueFactValue, IssueFacts } from './types';

export type { FieldMeaningKind } from './field-meanings';

/**
 * What a changed source detail means to the owner. Each skill declares the
 * meanings of the details it emits (see `field-meanings.ts`); this resolver
 * reads the generated binding for the observation's source and falls back to
 * rules that only look at a label's shape. The platform routes on `material`
 * and `catalogued`; the writers explain `noun`, `matters` and `means`.
 */
export type FieldSignificance =
  | { material: false; reason: ImmaterialReason }
  | {
      material: true;
      kind: FieldMeaningKind;
      /** The detail as the subject of a sentence: "the time allowed to correct the violation". */
      noun: string;
      /** Why a change matters, as a predicate: "moves the deadline for the required work". */
      matters: string;
      /** One or two plain sentences for the model: what the detail is and why a change matters. */
      means: string;
      unit?: 'days';
      /** True when the skill's declaration explains the detail; false when only its label's shape does. */
      catalogued: boolean;
    };

/** Where a detail came from; the skill that owns the source explains it. */
export type FieldScope = { source?: string | null; recordKind?: string | null };
/** The static binding by observation source; tests may pass a synthetic one. */
export type FieldMeaningBindings = Record<string, SkillFieldMeanings>;

/** Whether any skill has declared what this source's details mean. */
export function hasFieldMeanings(
  source: string | null | undefined,
  bindings: FieldMeaningBindings = fieldMeaningsBySource,
) {
  return Boolean(source && bindings[source]);
}

type Meaning = Extract<FieldSignificance, { material: true }>;
function materialize(
  declaration: Exclude<FieldMeaningDeclaration, { immaterial: ImmaterialReason }>,
): Meaning {
  const matters = declaration.matters ?? '';
  return {
    material: true,
    kind: declaration.kind,
    noun: declaration.noun,
    matters,
    means:
      declaration.means ?? `This is ${declaration.noun}.${matters ? ` A change ${matters}.` : ''}`,
    catalogued: true,
    ...(declaration.unit ? { unit: declaration.unit } : {}),
  };
}
const compiled = new Map<SkillFieldMeanings, Map<string, FieldSignificance>>();
function table(source: string | null | undefined, bindings: FieldMeaningBindings) {
  const meanings = source ? bindings[source] : undefined;
  if (!meanings) return null;
  let map = compiled.get(meanings);
  if (!map) {
    map = new Map();
    for (const declaration of meanings.declarations) {
      const value: FieldSignificance =
        'immaterial' in declaration
          ? { material: false, reason: declaration.immaterial }
          : materialize(declaration);
      for (const name of declaration.fields) map.set(fieldMeaningKey(name), value);
    }
    compiled.set(meanings, map);
  }
  return map;
}

/** Page and table containers describe the record's layout, never the record. */
export const CONTAINER_METADATA = new Set(['title', 'headers', 'links']);
/**
 * Keys of the platform's own record contract (`ParcelRecord`, `NormalizedPage`,
 * `SourceObservation`): layout, identity and fetch bookkeeping. A feed's own
 * column names belong to the skill that reads the feed.
 */
const GENERIC_IMMATERIAL = new Map<string, ImmaterialReason>(
  (
    [
      [
        'administrative',
        [
          ...CONTAINER_METADATA,
          'coverage',
          'kind',
          'locationScope',
          'sourceOrder',
          'parserVersion',
          'updatedAt',
          'rowId',
        ],
      ],
      ['identity', ['id', 'key', 'sourceUrl']],
    ] as const
  ).flatMap(([reason, names]) => names.map((name) => [fieldMeaningKey(name), reason] as const)),
);

const guess = (kind: FieldMeaningKind, noun: string, matters: string): Meaning => ({
  material: true,
  kind,
  noun,
  matters,
  means: `This is ${noun}.${matters ? ` A change ${matters}.` : ''}`,
  catalogued: false,
});

/** Presentation-only classification of one changed source detail. */
export function fieldSignificance(
  field: string,
  scope: FieldScope = {},
  bindings: FieldMeaningBindings = fieldMeaningsBySource,
): FieldSignificance {
  const raw = fieldPathTail(field);
  const k = fieldMeaningKey(raw);
  const declared = table(scope.source, bindings)?.get(k);
  if (declared) return declared;
  // "Address 2" means what the skill says "Address" means: a numbered repeat of a label.
  const base = k.replace(/\d+$/, '');
  const repeated = base && base !== k ? table(scope.source, bindings)?.get(base) : undefined;
  if (repeated) return repeated;
  const generic = GENERIC_IMMATERIAL.get(k);
  if (generic) return { material: false, reason: generic };
  // Only the label's shape is known here: the model may judge these edits.
  const lower = issueFieldLabel(raw).toLowerCase();
  if (/\b(?:phone|telephone|fax|e-?mail)\b/.test(lower))
    return { material: false, reason: 'contact' };
  if (
    /\b(?:amount|total|fee|fees|penalty|penalties|value|price|balance|cost|paid|due)\b/.test(lower)
  )
    return guess('amount', lower, 'changes an amount the city states for this record');
  if (/\b(?:date|deadline|expires|expiration)\b/.test(lower))
    return guess('date', lower, 'changes a date the city states for this record');
  if (/\b(?:status|disposition|stage|result|outcome)\b/.test(lower))
    return guess('status', 'the status', '');
  return guess('detail', lower, '');
}

export type FieldDelta = {
  field: string;
  before: string | null;
  after: string | null;
  /** Rows of a table were added or removed; the detail name is the table's key. */
  rows?: true;
  /** A whole object or list appeared, vanished or changed shape under this key. */
  structural?: true;
};

function object(value: IssueFactValue | undefined): value is Record<string, IssueFactValue> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}
function scalarRows(value: IssueFactValue[]) {
  return value.every(
    (row) =>
      row === null ||
      typeof row !== 'object' ||
      (Array.isArray(row) && row.every((cell) => cell === null || typeof cell !== 'object')),
  );
}
function text(value: IssueFactValue | undefined) {
  if (value === undefined || value === null) return null;
  return typeof value === 'object' ? stableSerialize(value) : String(value);
}
const IDENTITY_KEYS = ['key', 'id', 'rowId'];
/** Members of two lists matched by the first identity key every member carries uniquely. */
function pairByIdentity(before: IssueFactValue[], after: IssueFactValue[]) {
  for (const key of IDENTITY_KEYS) {
    const index = (items: IssueFactValue[]) => {
      const map = new Map<string, IssueFactValue>();
      for (const item of items) {
        const value = object(item) ? item[key] : undefined;
        if (typeof value !== 'string' || !value || map.has(value)) return null;
        map.set(value, item);
      }
      return map;
    };
    const left = index(before),
      right = index(after);
    if (!left || !right) continue;
    return [...new Set([...left.keys(), ...right.keys()])].map(
      (id) => [left.get(id), right.get(id)] as const,
    );
  }
  return null;
}
/** The members of each list that the other does not also hold, whatever their order. */
function unmatchedMembers(before: IssueFactValue[], after: IssueFactValue[]) {
  const counts = new Map<string, number>();
  for (const item of after) {
    const key = stableSerialize(item);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const left: IssueFactValue[] = [];
  for (const item of before) {
    const key = stableSerialize(item),
      count = counts.get(key) ?? 0;
    if (count > 0) counts.set(key, count - 1);
    else left.push(item);
  }
  const right = after.filter((item) => {
    const key = stableSerialize(item),
      count = counts.get(key) ?? 0;
    if (count > 0) counts.set(key, count - 1);
    return count > 0;
  });
  return [left, right] as const;
}

/**
 * Every leaf detail that differs between two fact trees, named by its source key.
 * A key `atomic` names (bookkeeping a skill declared immaterial, such as a coverage
 * or attributes object) is compared as one unit instead of being descended into.
 */
export function fieldDeltas(
  before: IssueFactValue | undefined,
  after: IssueFactValue | undefined,
  field = '',
  out: FieldDelta[] = [],
  atomic: (key: string) => boolean = () => false,
): FieldDelta[] {
  if (stableSerialize(before) === stableSerialize(after)) return out;
  if (object(before) && object(after)) {
    for (const child of new Set([...Object.keys(before), ...Object.keys(after)]))
      if (CONTAINER_METADATA.has(child) || atomic(child)) {
        if (stableSerialize(before[child]) !== stableSerialize(after[child]))
          out.push({ field: child, before: text(before[child]), after: text(after[child]) });
      } else fieldDeltas(before[child], after[child], child, out, atomic);
    return out;
  }
  if (Array.isArray(before) && Array.isArray(after)) {
    if (scalarRows(before) && scalarRows(after)) {
      // Rows of a table: reordering is nothing; a row added or removed is activity.
      const sorted = (rows: IssueFactValue[]) => rows.map(stableSerialize).sort().join('\n');
      if (sorted(before) !== sorted(after))
        out.push({ field, before: null, after: null, rows: true });
      return out;
    }
    // Pages, tables or activities: a member listed on both sides, in any order, is
    // nothing. The rest pair by a stable key when every member carries one, else by
    // position, so a feed that lists newest first reports only the member it edited.
    const [left, right] = unmatchedMembers(before, after);
    const pairs =
      pairByIdentity(left, right) ??
      Array.from({ length: Math.max(left.length, right.length) }, (_, i) => [left[i], right[i]]);
    for (const [l, r] of pairs) fieldDeltas(l, r, field, out, atomic);
    return out;
  }
  const structural =
    (before !== null && typeof before === 'object') ||
    (after !== null && typeof after === 'object');
  out.push({
    field,
    before: text(before),
    after: text(after),
    ...(structural ? { structural: true } : {}),
  });
  return out;
}

/**
 * Whether a change to one record is worth telling anyone. The source already
 * changed the record either way; only the owner's attention is at stake.
 */
export function changeSignificance(
  before: IssueFacts | null,
  after: IssueFacts,
  scope: FieldScope = {},
  bindings: FieldMeaningBindings = fieldMeaningsBySource,
) {
  const deltas = fieldDeltas(
    before ?? undefined,
    after,
    '',
    [],
    (key) => !fieldSignificance(key, scope, bindings).material,
  );
  const material = deltas.filter(
    (delta) =>
      delta.rows || delta.structural || fieldSignificance(delta.field, scope, bindings).material,
  );
  return { material: material.length > 0, deltas, materialDeltas: material };
}

/** The writer's view of one edited detail: a plain label and what it means. */
export function fieldChangeMeaning(
  field: string,
  scope: FieldScope = {},
): { label: string; meaning?: string } {
  const significance = fieldSignificance(field, scope);
  // A city label is untrusted text: it is bounded wherever it is shown or logged.
  const label = issueFieldLabel(field).slice(0, 60);
  return significance.material && significance.catalogued
    ? { label, meaning: significance.means }
    : { label };
}

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);
/** The longest shared opening of two texts, cut back to a word boundary. */
function commonWordPrefix(left: string, right: string) {
  let i = 0;
  while (i < left.length && i < right.length && left[i] === right[i]) i++;
  return left.slice(0, i).replace(/\S*$/, '');
}
/** The longest shared ending of two texts, cut forward to a word boundary. */
function commonWordSuffix(left: string, right: string) {
  let i = 0;
  while (i < left.length && i < right.length && left.at(-1 - i) === right.at(-1 - i)) i++;
  const suffix = left.slice(left.length - i);
  // Keep a suffix that begins a word in both texts; otherwise drop the partial word.
  const boundary = (text: string) => i === text.length || /\s/.test(text.at(-1 - i)!);
  return boundary(left) && boundary(right) ? suffix : suffix.replace(/^\S*\s?/, '');
}
const BOOLEAN_ON = /^(?:y|yes|true|x|checked|on|1)$/i;
const BOOLEAN_OFF = /^(?:n|no|false|unchecked|off|0)?$/i;

/**
 * One or two plain sentences for the deterministic writer: what changed, from
 * what to what, and why it matters. `clip` bounds and excerpts source text.
 */
export function describeFieldChange(
  field: string,
  before: string | null,
  after: string | null,
  clip: (value: string, max: number) => string = (value, max) =>
    value.length > max ? `${value.slice(0, max)}…` : value,
  scope: FieldScope = {},
): string {
  const significance = fieldSignificance(field, scope);
  const label = clip(issueFieldLabel(field), 35);
  if (!significance.material)
    return before === null
      ? `${label} was added: ${clip(after ?? '', 90)}.`
      : after === null
        ? `${label} was removed (it was ${clip(before, 60)}).`
        : `${label} changed from ${clip(before, 60)} to ${clip(after, 90)}.`;
  const { kind, matters } = significance;
  // A declared noun is the skill's own words; an undeclared one is the city's label, bounded.
  const noun = significance.catalogued ? significance.noun : label;
  const unit =
    significance.unit && [before, after].every((value) => value === null || /^\d+$/.test(value))
      ? ` ${significance.unit}`
      : '';
  const value = (raw: string | null, max: number) =>
    raw === null || raw === '' ? 'not recorded' : `${clip(raw, max)}${unit}`;
  const tail = matters ? ` That ${matters}.` : '';
  // The new wording is the consequence; the reader does not need it explained twice,
  // and when the city appended a sentence, the added sentence is what changed.
  const quoted = (raw: string, max: number) => clip(raw.trim(), max).replace(/\.$/, '');
  if (kind === 'description') {
    const was = (before ?? '').trim(),
      now = (after ?? '').trim();
    if (!was) return `The city added ${noun}: ${quoted(now, 160)}.`;
    if (!now) return `The city removed ${noun} (it was ${quoted(was, 60)}).`;
    if (now.startsWith(was))
      return `The city added to ${noun}: ${quoted(now.slice(was.length), 160)}.`;
    if (was.startsWith(now))
      return `The city shortened ${noun}. It no longer says: ${quoted(was.slice(now.length), 120)}.`;
    // Quote only the part that differs: strip what both versions share at either end.
    const head = commonWordPrefix(was, now);
    const tail = commonWordSuffix(was.slice(head.length), now.slice(head.length));
    const changed = now.slice(head.length, now.length - tail.length).trim();
    const dropped = was.slice(head.length, was.length - tail.length).trim();
    if (head.length + tail.length < 20)
      return `The city changed ${noun}. It now reads: ${quoted(now, 160)}.`;
    if (!changed) return `The city removed from ${noun}: ${quoted(dropped, 120)}.`;
    return changed.length < 25
      ? `The city changed ${noun}. It now reads: ${quoted(now, 160)}.`
      : `The city changed ${noun}. The new part reads: ${quoted(changed, 160)}.`;
  }
  if (kind === 'requirement') {
    const on = after !== null && BOOLEAN_ON.test(after.trim());
    const off = after === null || BOOLEAN_OFF.test(after.trim());
    const wasOn = before !== null && BOOLEAN_ON.test(before.trim());
    const wasOff = before === null || BOOLEAN_OFF.test(before.trim());
    // The record is named by its kind: a notice, a permit, a licence.
    const record = scope.recordKind
      ? `The ${propertyRecordKindLabel(scope.recordKind).toLowerCase()}`
      : 'The record';
    if (on && wasOff) return `${record} now includes ${noun}.${tail}`;
    if (off && wasOn) return `${record} no longer includes ${noun}.${tail}`;
  }
  if (kind === 'status')
    return `The status changed from ${value(before, 60)} to ${value(after, 90)}.`;
  // A link is never quoted: the reader learns what came or went, not where it is kept.
  const link = (raw: string | null) => raw !== null && /^(?:https?:\/\/|www\.)/i.test(raw.trim());
  if (before === null || before === '')
    return link(after)
      ? `${capitalize(noun)} was added.${tail}`
      : `${capitalize(noun)} was added: ${value(after, 90)}.${tail}`;
  if (after === null || after === '')
    return link(before)
      ? `${capitalize(noun)} was removed.`
      : `${capitalize(noun)} was removed (it was ${value(before, 60)}).`;
  if (link(before) || link(after)) return `${capitalize(noun)} was replaced.${tail}`;
  return `${capitalize(noun)} changed from ${value(before, 60)} to ${value(after, 90)}.${tail}`;
}
